import React, { useState, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { TextInput, SelectInput, CountrySelectDropdown, FormContainer } from "../../../shared";
import { Pattern, AxiosGraphQlAction, APPLICATION_LIST } from "../../../services";
import { connect } from "react-redux";
import { type AxiosResponse } from "axios";
import { getPersonaGroupList, setPersonaGroupList } from "../../reducers";
import { ERROR_CODE_LIST as codeList } from "../../../../utils/errCodes";
import { toast } from "react-toastify";

function Form(props: any) {
    const { formType, query, formData } = props;
    const defaultValues =
        formType === "update"
            ? {
                  ...formData,
                  inputEmail: "",
              }
            : {
                  personaName: "",
                  appName: "",
                  personaEmail: "",
                  country: "",
                  mailAddress: [],
                  inputEmail: "",
              };

    const [loading, setLoading] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        reset,
        resetField,
        getValues,
        setError,
        clearErrors,
    } = useForm({
        defaultValues,
    });

    const [duplicateAlert, setDuplicateAlert] = useState(false);

    const handleAddEmail = () => {
        const inputEmail = getValues(`personaEmail`);
        let isDuplicate = false;
        fields.map((field: any) => {
            let fieldValue: string = "";
            Object.entries(field).forEach(([key, value]) => {
                key !== "id" && (fieldValue += `${value}`);
            });
            if (fieldValue === inputEmail) {
                isDuplicate = true;
            }
            return null;
        });
        if (Pattern.email.test(inputEmail)) {
            if (!isDuplicate) {
                append(inputEmail);
                resetField(`personaEmail`);
            } else {
                setError("personaEmail", {});
                setDuplicateAlert(true);
                setTimeout(() => {
                    setDuplicateAlert(false);
                }, 3000);
            }
        } else {
            setError("personaEmail", {});
        }
    };

    const scrollRef = useRef(null);

    const onSubmit = handleSubmit((data) => {
        const inputEmail = getValues(`personaEmail`);
        const fieldEmails: string[] = data.mailAddress ? [...data.mailAddress] : [];

        fieldEmails.push(inputEmail);
        const duplicateIndexes: number[] = [];
        fieldEmails.forEach((item, index) => {
            if (fieldEmails.indexOf(item) !== index) {
                duplicateIndexes.push(index);
            }
        });

        if (duplicateIndexes.length > 0) {
            duplicateIndexes.forEach((index) => {
                setError(`mailAddress.${index}`, {});
            });
            setDuplicateAlert(true);
            setTimeout(() => {
                setDuplicateAlert(false);
                clearErrors("mailAddress");
            }, 3000);
        } else {
            setLoading(true);
            const currentElement: any = scrollRef.current;
            window.scrollTo({
                top: currentElement.offsetTop,
                behavior: "smooth",
            });
            const inputEmail = getValues(`personaEmail`);
            inputEmail &&
                inputEmail?.length !== 0 &&
                data.mailAddress.push(inputEmail) &&
                append(inputEmail);
            resetField(`personaEmail`);
            const variables =
                formType === "update"
                    ? {
                          id: data.id,
                          abbvieProPersona: {
                              personaName: data.personaName,
                              country: data.country,
                              appName: data.appName,
                              email: data.mailAddress,
                          },
                      }
                    : {
                          abbvieProPersona: {
                              personaName: data.personaName,
                              country: data.country,
                              appName: data.appName,
                              personaType: "Group",
                              audienceSpecialty: null,
                              email: data.mailAddress,
                          },
                      };

            AxiosGraphQlAction(variables, query)
                .then((response: AxiosResponse) => {
                    setLoading(false);
                    if (response.data.errors) {
                        const errMgs =
                            // @ts-expect-error: Unreachable code error
                            codeList[response.data.errors[0].errcode] ||
                            "Something went wrong. Please try again.";

                        toast.error(errMgs);
                    } else {
                        formType !== "update" && reset();
                        toast.success(
                            `Successfully ${formType === "update" ? "Updated" : "Created"}`
                        );

                        const personaGroupList: any = [];
                        props.personaGroupList.forEach((item: any, index: number) => {
                            if (item.id === variables.id) {
                                personaGroupList.push({
                                    ...item,
                                    id: variables.id,
                                    ...variables.abbvieProPersona,
                                });
                            } else {
                                personaGroupList.push(item);
                            }
                        });
                        props.setPersonaList(personaGroupList);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                    toast.error(error.message);
                });
        }
    });

    const { fields, append, remove } = useFieldArray({
        name: "mailAddress",
        control,
    });

    return (
        <FormContainer
            submitAction={onSubmit}
            submitButtonText={formType === "update" ? "Update Persona" : "Create Persona"}
            loading={loading}
            ref={scrollRef}
        >
            <div className="row">
                <div className="col-12">
                    <TextInput
                        id="personaName"
                        type="text"
                        labelText="Persona Name"
                        {...register("personaName", {
                            required: true,
                            pattern: Pattern.name,
                        })}
                        isInvalid={!!errors.personaName}
                        required
                    />
                </div>

                <div className="col-12">
                    <SelectInput
                        id="appName"
                        labelText="Application Name"
                        {...register("appName", {
                            required: true,
                        })}
                        options={APPLICATION_LIST}
                        isInvalid={!!errors.appName}
                        required
                    />
                </div>

                <div className="col-12">
                    <Controller
                        name="country"
                        render={({ field: { onChange, value } }) => (
                            <CountrySelectDropdown
                                id="country"
                                country={value}
                                labelText="Country"
                                selectCountry={onChange}
                                {...register("country", {
                                    required: true,
                                })}
                                required
                                isInvalid={!!errors.country}
                            />
                        )}
                        control={control}
                    />
                </div>
            </div>
            <div className="form-multifrield form-group mb-3">
                {fields.map((item: any, index: number) => {
                    return (
                        <div className="row" key={item.id}>
                            <div className="col">
                                <TextInput
                                    type="text"
                                    labelText="Email"
                                    {...register(`mailAddress.${index}` as const, {
                                        required: true,
                                        pattern: Pattern.email,
                                    })}
                                    // @ts-expect-error: Unreachable code error
                                    defaultValue={formData?.mailAddress[index]}
                                    isInvalid={
                                        !!(
                                            errors.mailAddress &&
                                            // @ts-expect-error: Unreachable code error
                                            errors?.mailAddress[index]
                                        )
                                    }
                                    required
                                />
                            </div>
                            <div className="col-lg-2 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-lg mt-3 w-100"
                                    onClick={() => {
                                        remove(index);
                                    }}
                                >
                                    X
                                </button>
                            </div>
                        </div>
                    );
                })}

                <div className="row">
                    <div className="col">
                        <TextInput
                            id="personaEmail"
                            type="text"
                            labelText="Email"
                            {...register("personaEmail", {
                                required: !(fields.length > 0),
                                pattern: Pattern.email,
                            })}
                            isInvalid={!!errors.personaEmail}
                            required
                        />
                    </div>
                    <div className="col-lg-2 mt-3">
                        <button
                            type="button"
                            className="btn btn-outline-info btn-lg mt-3 w-100"
                            onClick={() => {
                                handleAddEmail();
                            }}
                        >
                            Add
                        </button>
                    </div>
                    {duplicateAlert && (
                        <div className="col-12">
                            <p className="mb-0 text-danger small">Duplicate email.</p>
                        </div>
                    )}
                </div>
            </div>
        </FormContainer>
    );
}

const mapStateToProps = (state: any) => {
    return {
        personaGroupList: getPersonaGroupList(state),
    };
};
const mapDispatchToProps = (dispatch: any) => {
    return {
        setPersonaList: (personaList: any) => {
            dispatch(setPersonaGroupList(personaList));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
